import { useEffect, useState } from 'react'
import { useLocation } from 'wouter'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'
import toast from 'react-hot-toast'

export function AuthCallback() {
  const [, setLocation] = useLocation()
  const { loginWithToken } = useAuth()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<string>('')
  const [hasProcessed, setHasProcessed] = useState(false)

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed) return
    
    const handleCallback = async () => {
      setHasProcessed(true)
      try {
        const urlParams = new URLSearchParams(window.location.search)
        const token = urlParams.get('token')
        // const provider = urlParams.get('provider') // Not currently used
        const error = urlParams.get('error')

        if (error) {
          setStatus('error')
          setError(error === 'linkedin_auth_failed' ? 'LinkedIn authentication failed. Please try again.' : 'Authentication failed')
          return
        }

        if (!token) {
          setStatus('error')
          setError('No authentication token received')
          return
        }

        // Store the token and user data
        const result = await loginWithToken(token)
        
        if (result.success) {
          setStatus('success')
          
          // Check if this is a new user (for LinkedIn signup)
          const urlParams = new URLSearchParams(window.location.search)
          const provider = urlParams.get('provider')
          const isNewUser = urlParams.get('new_user') === 'true'
          
          if (provider === 'linkedin' && isNewUser) {
            toast.success('Welcome to SourcedIn! Your LinkedIn account has been connected successfully.')
            setTimeout(() => {
              setLocation('/welcome')
            }, 2000)
          } else if (provider === 'linkedin') {
            toast.success('Successfully logged in with LinkedIn!')
            setTimeout(() => {
              setLocation('/')
            }, 2000)
          } else {
            toast.success(result.message)
            setTimeout(() => {
              setLocation('/')
            }, 2000)
          }
        } else {
          setStatus('error')
          setError(result.message)
        }
      } catch (error) {
        console.error('Auth callback error:', error)
        setStatus('error')
        setError('An unexpected error occurred during authentication')
      }
    }

    handleCallback()
  }, [hasProcessed, loginWithToken, setLocation])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-primary/10 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-lg gradient-bg flex items-center justify-center">
            <span className="text-white font-bold text-lg">SI</span>
          </div>
          <CardTitle className="text-2xl">Authentication</CardTitle>
          <CardDescription>
            {status === 'loading' && 'Completing your sign in...'}
            {status === 'success' && 'Sign in successful!'}
            {status === 'error' && 'Sign in failed'}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          {status === 'loading' && (
            <div className="space-y-4">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-sm text-muted-foreground">
                Please wait while we complete your authentication...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-4">
              <CheckCircle className="h-8 w-8 mx-auto text-green-500" />
              <p className="text-sm text-muted-foreground">
                You will be redirected to the dashboard shortly...
              </p>
              <Button onClick={() => setLocation('/')} className="w-full">
                Go to Dashboard
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <XCircle className="h-8 w-8 mx-auto text-red-500" />
              <p className="text-sm text-red-600">{error}</p>
              <div className="space-y-2">
                <Button onClick={() => setLocation('/login')} className="w-full">
                  Try Again
                </Button>
                <Button variant="outline" onClick={() => setLocation('/')} className="w-full">
                  Go to Home
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
} 